import { Paginated } from '../../../core/models/paginated.model';

export type OrderStatus = 'confirmed' | 'onTheWay' | 'delivered' | 'partial' | 'cancelled';

/** Confirmed and out-for-delivery orders can be edited; the others are closed (view only). */
export function isOrderEditable(status: OrderStatus): boolean {
  return status === 'confirmed' || status === 'onTheWay';
}
/** Channel the order came in from. */
export type OrderSource = 'retailApp' | 'teleSales' | 'salesRep';
/** This phase is cash on delivery only. */
export type OrderPayment = 'cash';

export interface OrderPromotion {
  code: string;
  kind: 'percent' | 'amount';
  /** Percent off, or EGP off per carton. */
  value: number;
}

export interface OrderLine {
  skuId: string;
  code: string;
  name: string;
  nameAr: string;
  imageUrl: string;
  unit: 'carton' | 'shrink' | 'crate';
  /** Pieces in one unit. */
  packSize: number;
  quantity: number;
  /** List price of one unit, before promotions. */
  unitPrice: number;
  /** Units in stock at the last SAP check. */
  stock: number;
  promotion: OrderPromotion | null;
  /** After promotions. */
  total: number;
}

export interface OrderCustomer {
  id: string;
  shop: string;
  shopAr: string;
  area: string;
  areaAr: string;
  /** ISO date-time the shop became a customer. */
  since: string;
  verified: boolean;
  verifiedAt?: string;
  /** The shop user who placed the order (all shop users are equal). */
  orderedBy: string;
  orderedByAr: string;
  phone: string;
  address: string;
  addressAr: string;
  landmark: string;
  landmarkAr: string;
  location: { lat: number; lng: number } | null;
  /** SAP business partner code. */
  sapCode?: string;
}

export interface OrderRunsheet {
  id: string;
  salesRep: string;
  salesRepAr: string;
}

/** Who did something on the order; also the timeline filter. */
export type OrderActor = 'teleSales' | 'system' | 'shop';

export type OrderEventKind =
  | 'sent'
  | 'promotionsApplied'
  | 'opened'
  | 'called'
  | 'edited'
  | 'orderEdited'
  | 'lineRemoved'
  | 'customerVerified'
  | 'approved'
  | 'sapOrderCreated'
  | 'runsheetAdded'
  | 'createdByTeleSales'
  | 'dispatched'
  | 'delivered'
  | 'partiallyDelivered'
  | 'cancelled';

export interface OrderEvent {
  id: string;
  kind: OrderEventKind;
  actor: OrderActor;
  at: string;
  /** Person who did it, when it was a person. */
  by?: string;
  byAr?: string;
  /** Values for the event texts: names, codes, money amounts (`saved`, `from`, `to`). */
  params?: Record<string, string | number>;
}

/** `GET /orders/:id` (B16, B13 when it came from a request). */
export interface OrderDetail {
  id: string;
  status: OrderStatus;
  source: OrderSource;
  /** Set when the order was created from a request. */
  requestId?: string;
  requestedAt: string;
  approvedAt: string;
  approvedBy: string;
  approvedByAr: string;
  /** ISO date. */
  deliveryDate: string;
  cutoffTime: string;
  cancelCutoffTime: string;
  runsheet: OrderRunsheet | null;
  customer: OrderCustomer;
  lines: OrderLine[];
  subtotal: number;
  discount: number;
  total: number;
  payment: OrderPayment;
  /** Code the shop gives the sales rep on delivery. */
  handoverCode: string;
  note: string;
  noteAr: string;
  noteAt: string;
  events: OrderEvent[];
}

/** A row of the orders list (B15). */
export interface OrderSummary {
  id: string;
  createdAt: string;
  shop: string;
  shopAr: string;
  zone: string;
  zoneAr: string;
  items: number;
  total: number;
  payment: OrderPayment;
  /** ISO date. */
  deliveryDate: string;
  source: OrderSource;
  status: OrderStatus;
}

/** When the order was placed. */
export type OrderedWithin = '' | 'today' | 'yesterday' | 'week';

/** `GET /orders` query. Empty strings mean "any". */
export interface OrdersQuery {
  page: number;
  pageSize: number;
  status: OrderStatus | '';
  ordered: OrderedWithin;
  zone: string;
  source: OrderSource | '';
  /** ISO date. */
  delivery: string;
}

export interface OrderZone {
  id: string;
  label: string;
  labelAr: string;
}

/** `GET /orders`: one page plus what the toolbar needs. */
export interface OrdersPage extends Paginated<OrderSummary> {
  /** Orders per status for the current filters (status aside). Missing: not known (not in the API yet). */
  counts: Partial<Record<OrderStatus | 'all', number>>;
  /** Requests waiting for a tele-sales approval. */
  requestsWaiting: number;
  /** Delivery dates that have orders, soonest first. */
  deliveryDates: string[];
}

/** Why an order was edited (B17), managed in Settings. */
export interface OrderEditReason {
  id: string;
  label: string;
  labelAr: string;
}

/** `PUT /orders/:id` (B17). Lines left out or with quantity 0 are removed. */
export interface UpdateOrderBody {
  lines: { skuId: string; quantity: number }[];
  reasonIds: string[];
  note: string;
  notifyShop: boolean;
}

/** A SKU found by the "add an item" search (`GET /catalog/skus`). */
export interface SkuOption {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  imageUrl: string;
  /** Carton price after running promotions. */
  unitPrice: number;
  stock: number;
}

/** A customer found by the B18 customer search (`GET /new-order/customers`). */
export interface CustomerOption {
  id: string;
  shop: string;
  shopAr: string;
  area: string;
  areaAr: string;
  /** SAP business partner code. */
  sapCode: string;
  phone: string;
  verified: boolean;
  /** Blocked shops are hidden from sales reps and can't order. */
  blocked?: boolean;
  /** The API's status name in the request language, e.g. "Blocked". */
  statusLabel?: string;
}

/** A product priced on the shop's list for ordering (`GET /new-order/products`, B18). */
export interface CatalogProduct {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  imageUrl: string;
  unit: OrderLine['unit'];
  packSize: number;
  /** Price of one unit before promotions. */
  listPrice: number;
  /** Price of one unit after the running promotion. */
  netPrice: number;
  promotion: OrderPromotion | null;
  stock: number;
}

/** When an order created now delivers (`POST /new-order/quote`). */
export interface DeliverySlot {
  /** ISO date. */
  deliveryDate: string;
  cutoffTime: string;
  beforeCutoff: boolean;
}

/** `GET /new-order/last-order`: lines that can still be ordered. */
export interface LastOrder {
  orderId: string;
  lines: { skuId: string; quantity: number }[];
}

/** `POST /new-order/submit` (B18): a tele-sales order, created approved, paid cash on delivery. */
export interface CreateOrderBody {
  customerId: string;
  lines: { skuId: string; quantity: number }[];
}
